import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox, Popover } from 'antd';
import { DownOutlined, ExclamationCircleOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';

import { shouldShowDisabledItemsMessage } from './customLens.helpers';
import { CUSTOM_LENS_DROPDOWN_POPOVER_INNER_STYLE, CUSTOM_LENS_SELECTION_POPOVER_CLASS, CUSTOM_LENS_UI } from './customLens.ui';
import type { SelectionCountDropdownProps } from './customLens.types';

type DisabledItemsWarningProps = {
    message: string;
};

const DisabledItemsWarning: FC<DisabledItemsWarningProps> = ({ message }) => (
    <div className="mb-2 flex items-start gap-1.5 text-orangeFont">
        <ExclamationCircleOutlined className="mt-0.5 shrink-0 text-sm" />
        <p className="m-0 text-smallWord leading-5">{message}</p>
    </div>
);

const SelectionCountDropdown: FC<SelectionCountDropdownProps> = ({
    field,
    selectedOptions,
    disabledOptions = [],
    disabledItemsMessage,
    onChange,
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [draftSelection, setDraftSelection] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setDraftSelection(selectedOptions);
            setSearchQuery('');
        }
    }, [open, selectedOptions]);

    const displayText = selectedOptions.length === 0 ? 'None' : `${selectedOptions.length} selection(s)`;
    const filteredOptions = field.options.filter((option) =>
        option.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    const apiDisabledOptions = useMemo(
        () => field.optionMeta.filter((option) => option.isDisabled).map((option) => option.label),
        [field.optionMeta],
    );
    const disabledSet = useMemo(
        () => new Set([...disabledOptions, ...apiDisabledOptions]),
        [apiDisabledOptions, disabledOptions],
    );
    const showDisabledItemsMessage = shouldShowDisabledItemsMessage(
        disabledItemsMessage,
        field.options,
        disabledOptions,
    );

    const handleToggleOption = (option: string, checked: boolean) => {
        if (checked && disabledSet.has(option)) return;
        setDraftSelection((prev) =>
            checked ? [...prev, option] : prev.filter((item) => item !== option),
        );
    };

    const handleAdd = () => {
        onChange(draftSelection.filter((item) => !disabledSet.has(item)));
        setOpen(false);
    };

    const popoverContent = (
        <div className="box-border w-full p-3">
            <div className="mb-4 border-b border-borderGreyAlt pb-4">
                <div className="flex items-stretch rounded border border-borderGreyAlt">
                    <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="min-w-0 flex-1 rounded border-none bg-transparent px-2 py-1.5 text-smallWord outline-none placeholder:text-fontGreyAlt1"
                    />
                    <div className="flex shrink-0 items-center border-l border-borderGreyAlt px-2">
                        <SearchOutlined className="text-sm text-purpleFont" />
                    </div>
                </div>
            </div>

            {showDisabledItemsMessage && disabledItemsMessage && (
                <DisabledItemsWarning message={disabledItemsMessage} />
            )}

            <div className="max-h-[200px] overflow-y-auto">
                {filteredOptions.length === 0 ? (
                    <p className="m-0 py-4 text-center text-smallWord text-fontGreyAlt1">No matching items</p>
                ) : (
                    <div className="flex flex-col gap-0.5">
                        {filteredOptions.map((option) => {
                            const isDisabled = disabledSet.has(option);
                            return (
                                <label
                                    key={option}
                                    className={`flex items-center gap-2 rounded  ${
                                        isDisabled
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'cursor-pointer hover:bg-bgBackground'
                                    }`}
                                >
                                    <Checkbox
                                        className="[&_.ant-checkbox-inner]:border-borderGrey"
                                        checked={draftSelection.includes(option)}
                                        disabled={isDisabled}
                                        onChange={(e) => handleToggleOption(option, e.target.checked)}
                                    />
                                    <span className="text-[14px] text-fontColor">{option}</span>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-2 flex justify-end gap-2 border-t border-borderGreyAlt pt-4">
                <button
                    type="button"
                    className="rounded bg-purpleFont px-[15px] py-[5px] text-[14px] font-medium text-white hover:opacity-90"
                    onClick={handleAdd}
                >
                    Add
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex items-center justify-between gap-3">
            <span
                className="shrink-0 whitespace-nowrap text-smallWord text-blackAlt"
                style={{ width: CUSTOM_LENS_UI.FIELD_LABEL_WIDTH }}
            >
                {field.label}:
            </span>
            <Popover
                trigger="click"
                open={open}
                onOpenChange={setOpen}
                placement="bottomLeft"
                align={{ offset: [...CUSTOM_LENS_UI.DROPDOWN_POPOVER_OFFSET] }}
                showArrow={false}
                overlayClassName={CUSTOM_LENS_SELECTION_POPOVER_CLASS}
                overlayInnerStyle={CUSTOM_LENS_DROPDOWN_POPOVER_INNER_STYLE}
                getPopupContainer={(triggerNode) =>
                    triggerNode.closest('.ant-modal-content') ?? document.body
                }
                content={popoverContent}
            >
                <button
                    type="button"
                    style={{ width: CUSTOM_LENS_UI.DROPDOWN_WIDTH }}
                    className={`flex h-8 items-center justify-between rounded-md border bg-white px-3 text-left text-smallWord text-fontColor ${
                        open ? 'border-purpleFont' : 'border-borderGrey hover:border-purpleFont'
                    }`}
                >
                    <span className="text-blackAlt">{displayText}</span>
                    {open ? (
                        <UpOutlined className="text-[10px] text-fontGreyAlt1" />
                    ) : (
                        <DownOutlined className="text-[10px] text-fontGreyAlt1" />
                    )}
                </button>
            </Popover>
        </div>
    );
};

export default SelectionCountDropdown;
